import {data} from "azure-maps-control";
import {lonLatToMercator, mercatorToLonLat} from "@esWebAPI/es/map/util";

import type {AzureBaseMap} from "../map/module/AzureBaseMap";
import type {LatLng, xy} from "@esWebAPI/es/model/Models";

export class NavUtils {

    /*
    Size of basemap tiles
    @see https://learn.microsoft.com/en-us/azure/azure-maps/zoom-levels-and-tile-grid
     */
    private static readonly WORLD_EXTENT = 40075017;
    private static readonly TILE_SIZE = 512;

    public static zoomToResolution(zoom: number) {
        const r = this.WORLD_EXTENT / this.TILE_SIZE;
        return r / Math.pow(2, zoom);
    }

    public static resolutionToZoom(resolution: number) {
        const r = this.WORLD_EXTENT / this.TILE_SIZE;
        const zoom = Math.log2( r / resolution );
        return +zoom.toFixed(1);
    }

    /**
     * Convert single geographic coordinate to pixel coordinate
     * e.g.:
     *     NavUtils.geoToPixel(esApp.map.baseMap, feat.geometry.coordinates);
     *     NavUtils.geoToPixel(esApp.map.baseMap, [-6.1236, 53.4350])
     * @param baseMap AzureBaseMap
     * @param coordinate array of coords [lng, lat]
     */
    public static geoToPixel(baseMap: AzureBaseMap, coordinate: [number, number]): xy {
        // reuse toPx
        //const rs_old = baseMap.map.map.project(coordinate);
        const rs = NavUtils.toPx(baseMap, coordinate);
        return {x: rs[0], y: rs[1]};
    }

    /**
     * Convert single pixel coordinate to geographic coordinate
     * e.g.:
     *     NavUtils.pixelToGeo(esApp.map.baseMap, {x: 474.36, y: 813.64});
     * @param baseMap AzureBaseMap
     * @param pixelCoord xy e.g. {x: 704, y, 320}
     */
    public static pixelToGeo(baseMap: AzureBaseMap, pixelCoord: xy): LatLng {
        // reuse fromPx
        ///const rs_old = baseMap.map.map.unproject(pixelCoord); // e.g. {lng: -6.123699999999218, lat: 53.43501737662103}
        const rs = NavUtils.fromPx(baseMap, [pixelCoord.x, pixelCoord.y]); // e.g. [-6.123699999999218, 53.43501737662103]
        return {lng: rs[0], lat: rs[1]};
    }

    /**
     * Transforms from Pixels to Positions (~~LatLon~~ LonLat)
     * @param baseMap AzureBaseMap
     * @param coords screen coordinates e.g. [704, 320]
     * @param offset
     * @param length
     * @param result [lng, lat] e.g. [-6.1233, 53.4359]
     */
    public static fromPx(baseMap: AzureBaseMap, coords: number[], offset?: number, length?: number, result?: number[]): [number, number] {
        if (!offset) {
            offset = 0;
        }
        if ((!length) && (length !== 0)) {
            length = coords.length;
        }
        if (!result) {
            result = [];
        }
        length += offset;
        for (let i = offset; i < length;) {
            const x = coords[i++];
            const y = coords[i++];

            // validate coordinates before unprojecting to prevent NaN errors
            if (!isFinite(x) || !isFinite(y)) {
                es.log.warn("NavUtils.fromPx: Invalid pixel coordinates (NaN or Infinity)", {x, y, coords});
                continue; // skip - it's going to fail anyway
            }

            result.push(...baseMap.map.pixelsToPositions([[x,y]])[0]);
        }
        return result;
    }

    /**
     * Transforms from Positions (~~LatLon~~ LonLat) to Pixels
     * @param baseMap AzureBaseMap
     * @param coords screen coordinates e.g. [-6.1233, 53.4359]
     * @param offset
     * @param length
     * @param result e.g. []
     */
    public static toPx(baseMap: AzureBaseMap, coords: number[], offset?: number, length?: number, result?: number[]): [number, number] {
        if (!offset) {
            offset = 0;
        }
        if ((!length) && (length !== 0)) {
            length = coords.length;
        }
        if (!result) {
            result = [];
        }

        length += offset;
        for (let i = offset; i < length;) {
            const x = coords[i++];
            const y = coords[i++];
            result.push(...baseMap.map.positionsToPixels([[x,y]])[0]);
        }
        return result;
    }

    public static fromPixel(coord: number[], resolution: number, center: number[], size: [number, number]) {
        center = lonLatToMercator(center);
        const cx = center[0];
        const cy = center[1];
        const ox = size[0] / 2;
        const oy = size[1] / 2;
        const x = coord[0];
        const y = coord[1];
        return mercatorToLonLat([(((x - ox) * resolution) + cx), (cy - ((y - oy) * resolution))]);
    }

    public static calculateBoundsFor(resolution: number, center: [number, number], size: [number, number]) {
        const northEast = this.fromPixel([size[0], 0], resolution, center, size);
        const southWest = this.fromPixel([0, size[1]], resolution, center, size);
        return data.BoundingBox.fromLatLngs([northEast, southWest]);

    }

    /**
     * 
     * @param resolution
     */
    public static getZoomForResolution(resolution: number){
        const r = 40075160 / 256;
        //const r = 40075016.686 / 256;
        let zoom = Math.log2( r / resolution ) - 1;
        zoom = +zoom.toFixed(1);
        return Math.max(zoom, 1); // TOR9-14906 - We don't really support zoom level 0
    }
}

